import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import Loader from "../Loader";
import KeycloakContext from "../../context";
import { initKeycloak } from "../../keycloakAxios";
import { useUserActivity } from "../../hooks";
import { isServer } from "../../utils/other";

const useInitKeycloak = ({ configUrl, options }) => {
  const [keycloak, setKeycloak] = useState();
  useEffect(() => {
    let isSubscribed = true;
    (async function init() {
      // eslint-disable-next-line no-underscore-dangle
      let _keycloak;
      try {
        if (!isServer()) {
          _keycloak = await initKeycloak(configUrl, options);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Keycloak]: Failed to initialize keycloak", err);
      }
      if (isSubscribed) {
        setKeycloak(_keycloak);
      }
    })();

    return () => {
      isSubscribed = false;
    };
    // Inititialization should happen only on mount component
    // eslint-disable-next-line
  }, []);

  return keycloak;
};

const KeycloakProvider = ({
  children,
  loader,
  options,
  configUrl,
  onLogout,
  trackUserActivity,
  sessionIdleMilliseconds,
  showChildrenWhileInitializing
}) => {
  const [logoutTimer, setLogoutTimer] = useState();
  const keycloak = useInitKeycloak({ configUrl, options });

  useUserActivity(() => {
    if (!keycloak) {
      return;
    }
    if (trackUserActivity) {
      keycloak.validateToken();
    }

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }

    if (sessionIdleMilliseconds) {
      setLogoutTimer(setTimeout(() => keycloak.validateToken(), sessionIdleMilliseconds));
    }
  }, 10000);

  useEffect(() => {
    if (!keycloak) {
      return;
    }
    keycloak.onAuthLogout = () => {
      if (onLogout) {
        // for what case need skip native keycloak logout
        onLogout();
      } else {
        keycloak.logout();
      }
    };
  }, [keycloak, onLogout]);

  const context = keycloak
    ? {
        getUserData: keycloak.getUserData,
        keycloak: keycloak.kc,
        // Remove isAuthenticated next release
        isAuthenticated: keycloak.getDeprecatedAuthenticated(),
        login: keycloak.login,
        logout: keycloak.logout
      }
    : null;

  const renderContent = () => {
    if (keycloak || showChildrenWhileInitializing) {
      return children;
    }
    return loader;
  };

  return (
    <KeycloakContext.Provider value={context}>
      {typeof children === "function" ? children(context) : renderContent()}
    </KeycloakContext.Provider>
  );
};

KeycloakProvider.displayName = "KeycloakProvider";

KeycloakProvider.defaultProps = {
  loader: <Loader />,
  onLogout: undefined,
  trackUserActivity: false,
  options: {
    onLoad: "check-sso"
  },
  sessionIdleMilliseconds: undefined,
  showChildrenWhileInitializing: false
};

KeycloakProvider.propTypes = {
  /** Anything that can be rendered  */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /** React element that can be rendered when user in authentication process */
  loader: PropTypes.element,
  /** Url of the keycloak adapter configurations json file */
  configUrl: PropTypes.string,
  /** Keycloak js provider configurations  */
  options: PropTypes.object,
  /** If onLogout is passed, in case of token expired onLogout will be called, otherwise redirected to login page. */
  onLogout: PropTypes.func,
  /** Track "mousedown", "mousemove", "keypress", "scroll", "touchmove" events and refresh auth token */
  trackUserActivity: PropTypes.bool,
  /** SSO Session Idle logout after this time when the user is not active (Need to set the same time, which is specified in the administrative module Keycloak and need to specify "trackUserActivity") */
  sessionIdleMilliseconds: PropTypes.number,
  /** Show children while initializing  */
  showChildrenWhileInitializing: PropTypes.bool
};

export default KeycloakProvider;
